import { useEffect, useState } from 'react';
import { useApi } from './useApi';
import { useAuth } from './useAuth';
import API_ENDPOINTS from '../constants/apiEndpoints';

/**
 * Polls the count of Etsy product templates that were backfilled to placeholder
 * values by the price/quantity NOT NULL migrations and still need a real value
 * from the seller. Backed by GET /api/v1/settings/product-template/needs-attention.
 *
 * Returns { count, items, refresh }. `count` is null until the first fetch
 * resolves so the UI can distinguish "still loading" from "verified zero".
 */
export const useTemplatesNeedingAttention = ({ pollIntervalMs = 60000 } = {}) => {
  const api = useApi();
  const { isUserAuthenticated } = useAuth();
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(null);

  const fetchOnce = async () => {
    try {
      const response = await api.get(`${API_ENDPOINTS.SETTINGS.PRODUCT_TEMPLATE}/needs-attention`);
      const list = Array.isArray(response) ? response : [];
      setItems(list);
      setCount(list.length);
    } catch {
      // Endpoint missing or auth lapse — keep prior state, surface zero so the
      // badge doesn't flap if the deploy hasn't picked up the new endpoint yet.
      setCount(prev => prev ?? 0);
    }
  };

  useEffect(() => {
    if (!isUserAuthenticated) {
      setCount(null);
      setItems([]);
      return undefined;
    }
    fetchOnce();
    const id = setInterval(fetchOnce, pollIntervalMs);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthenticated, pollIntervalMs]);

  return { count, items, refresh: fetchOnce };
};

export default useTemplatesNeedingAttention;
